/**
 * ExistingDebtInputs - Core Component (Production-Ready)
 * Clean input component for existing debt payment schedule without demo UI chrome.
 * Used in: Project workflow pages
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DOMAIN KNOWLEDGE: Existing Debt Payment Schedules (Municipal Bonds)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This component manages payment schedules for existing municipal debt projects
 * that will overlap with the new bond being planned. Understanding existing
 * obligations is critical for calculating available revenue capacity.
 *
 * Key Concepts:
 *
 * 1. REVERSE AMORTIZATION
 *    Municipal bonds calculate interest differently than typical amortization:
 *    - Start from the FINAL payment and work backwards
 *    - Each payment's interest includes:
 *      a) Principal Interest (interest on this payment's principal)
 *      b) Interest Payment from next payment (cumulative future obligations)
 *    - Formula: Interest Payment = Principal Interest + Interest Payment (next)
 *    - Even payments with zero principal still accumulate interest from future
 *    - Uses 30/360 day count convention (semi-annual = 180/360 = rate/2)
 *
 *    Example (3 payments, 5% rate):
 *    Payment 3: $1M principal → $25K interest (1M × 0.05/2)
 *               Interest Payment = $25K + $0 (final) = $25K
 *    Payment 2: $1M principal → $25K interest
 *               Interest Payment = $25K + $25K = $50K
 *    Payment 1: $1M principal → $25K interest
 *               Interest Payment = $25K + $50K = $75K
 *
 * 2. FISCAL YEAR AGGREGATION
 *    - Payments are grouped by fiscal year end date
 *    - Michigan fiscal years: July 1 → June 30 (summer) or Dec 1 → Nov 1 (winter)
 *    - Total debt service per fiscal year affects available revenue capacity
 *    - Only the final payment in each fiscal year shows the total
 *
 * 3. PAYMENT SCHEDULE GENERATION
 *    - Schedule is dynamically generated from DateRecords
 *    - Users select final payment date → generates all payments up to that date
 *    - Preserves existing data when regenerating (only adds/removes rows)
 *    - Each payment has: date, principal, interest rate, calculated interest
 *
 * 4. AUTO-MULTIPLY FEATURE
 *    - Domain-specific UX: users enter principal in thousands
 *    - Example: User types "1610" → stored as "1610000" on blur
 *    - Toggle between "Enter In 1000's" and "Enter Exact Amount"
 *    - Max value check: prevents values > $20M after multiply
 *    - Reduces data entry errors for large numbers
 *
 * 5. KEYBOARD NAVIGATION
 *    - Table interface requires efficient navigation
 *    - Arrow keys: Up/Down (same column), Left/Right (between columns)
 *    - Enter: Moves to next field (tab-like behavior)
 *    - Skips locked dates (May/November lock filters)
 *    - Auto-selects content on focus for quick replacement
 *
 * 6. LOCK CONTROLS (May/November Dates)
 *    - Filters out specific months from display
 *    - Use case: Focus on November-only or May-only payments
 *    - Locked dates still exist in data, just hidden from UI
 *    - Keyboard navigation skips locked dates
 *
 * 7. QUICK FILL TEMPLATES
 *    - Pre-defined payment schedules (available via dev utilities menu)
 *    - Matches template dates to available payment dates
 *    - Fills in principal and interest rate values
 *    - Clears existing calculations (triggers recalculation)
 *    - Note: Quick fill buttons are now in the dev utilities floating button
 *      (bottom-right corner, visible to super admins)
 *
 * Why This Matters:
 * - Existing debt reduces available revenue for new bonds
 * - Must account for ALL future obligations when sizing new bonds
 * - Reverse amortization ensures accurate total debt service
 * - Fiscal year totals feed into DRF (Debt Retirement Fund) calculations
 * - Inaccurate existing debt = over-sized or under-sized new bond
 *
 * Processing Pipeline Impact:
 * - Existing debt payments are subtracted from revenue capacity
 * - Affects maximum bond size calculations
 * - Influences DRF balance requirements
 * - Must be accurate or entire bond sizing is wrong
 *
 * Common Mistakes:
 * ❌ Don't use simple interest (must use reverse amortization)
 * ❌ Don't ignore zero-principal payments (they carry interest forward)
 * ❌ Don't recalculate on every keystroke (only on blur for performance)
 * ❌ Don't mix fiscal years when aggregating totals
 * ✅ Always work backwards from final payment
 * ✅ Always use 30/360 day count convention
 * ✅ Always include interest from future payments
 *
 * Auto-calculation behavior:
 * - When principal payment changes → blur triggers reverse amortization
 * - When interest rate changes → blur triggers reverse amortization
 * - When payment schedule regenerated → clears calculations
 * - When tab switches → clears calculations (new project context)
 * - Calculations saved to form for persistence
 *
 * For More Context:
 * @see docs/guides/production-input-component-pattern.md - Component pattern
 * @see apps/web/src/utils/reverseAmortization.ts - Calculation implementation
 * @see apps/web/src/builders/existing-debt/ExistingDebtBuilder.ts - Validation
 * @see apps/web/src/builders/existing-debt/useExistingDebtInputs.ts - Business logic hook
 */

import { Box, Text, VStack } from '@chakra-ui/react';
import type React from 'react';
import type {
  DateInputsForm,
  DateRecord,
  ExistingDebtInput,
  ExistingDebtInputForm,
  ExistingDebtPaymentForm,
  ExistingDebtPaymentInput,
} from '~/types';
import { ExistingDebtFooter } from './ExistingDebtFooter';
import { ExistingDebtHeader } from './ExistingDebtHeader';
import { ExistingDebtPaymentTable } from './ExistingDebtPaymentTable';
import { useExistingDebtInputs } from './useExistingDebtInputs';

interface ExistingDebtInputsProps {
  values: ExistingDebtInputForm;
  onUpdate: (values: ExistingDebtInputForm) => void;
  availablePaymentDates: DateRecord[];
  dateInputs?: DateInputsForm;
  showValidation?: boolean;
  isDisabled?: boolean;
}

export const ExistingDebtInputs: React.FC<ExistingDebtInputsProps> = ({
  values,
  onUpdate,
  availablePaymentDates,
  dateInputs,
  showValidation = false,
  isDisabled = false,
}) => {
  // Extract all business logic to custom hook
  const { handlers, computed, state } = useExistingDebtInputs({
    values,
    onUpdate,
    availablePaymentDates,
    dateInputs,
    showValidation,
    isDisabled,
  });

  // Destructure for cleaner code
  const {
    handleNameChange,
    handlePaymentChange,
    handlePrincipalPaymentBlur,
    handleInterestRateBlur,
    // Note: handleQuickFill is defined in the hook and used by the event listener (useExistingDebtInputs.ts:425-438)
    // for dev utilities quick fill. It's not needed in this component's JSX.
    generatePaymentSchedule,
    handleClearAll,
    handleKeyDown,
    isPaymentDateLocked,
    getInputRef,
    setFinalPaymentDate,
    setPaymentDateSearchQuery,
    setAutoMultiplyEnabled,
    setLockMayDates,
    setLockNovemberDates,
    setShowClearAllDialog,
    // Focus management
    handleInputFocus,
    handleInputBlur,
    isNavigating,
  } = handlers;

  const {
    calculatedInterestPayments,
    hasCalculatedReverseAmortization,
    fiscalYearTotals,
    fiscalYearColumnTitle,
    runningTotals,
    filteredPaymentDateOptions,
    validationErrors,
    allFieldsTouched,
  } = computed;

  const {
    finalPaymentDate,
    paymentDateSearchQuery,
    autoMultiplyEnabled,
    lockMayDates,
    lockNovemberDates,
    showClearAllDialog,
  } = state;

  if (availablePaymentDates.length === 0) {
    return (
      <VStack align="stretch" gap={6}>
        <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
          No payment dates available. Please configure project dates first.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack
      align="stretch"
      gap={6}
      border="1px solid"
      borderColor="gray.200"
      p={4}
      borderRadius="md"
      bg={isDisabled ? 'gray.50' : undefined}
      pointerEvents={isDisabled ? 'none' : undefined}
      opacity={isDisabled ? 0.8 : 1}
    >
      <ExistingDebtHeader
        values={values}
        finalPaymentDate={finalPaymentDate}
        setFinalPaymentDate={setFinalPaymentDate}
        paymentDateSearchQuery={paymentDateSearchQuery}
        setPaymentDateSearchQuery={setPaymentDateSearchQuery}
        filteredPaymentDateOptions={filteredPaymentDateOptions}
        isDisabled={isDisabled}
        onNameChange={handleNameChange}
        onGenerateSchedule={generatePaymentSchedule}
      />

      <ExistingDebtPaymentTable
        values={values}
        dateInputs={dateInputs}
        availablePaymentDates={availablePaymentDates}
        calculatedInterestPayments={calculatedInterestPayments}
        hasCalculatedReverseAmortization={hasCalculatedReverseAmortization}
        fiscalYearTotals={fiscalYearTotals}
        fiscalYearColumnTitle={fiscalYearColumnTitle}
        runningTotals={runningTotals}
        autoMultiplyEnabled={autoMultiplyEnabled}
        setAutoMultiplyEnabled={setAutoMultiplyEnabled}
        lockMayDates={lockMayDates}
        setLockMayDates={setLockMayDates}
        lockNovemberDates={lockNovemberDates}
        setLockNovemberDates={setLockNovemberDates}
        isPaymentDateLocked={isPaymentDateLocked}
        getInputRef={getInputRef}
        handlePaymentChange={handlePaymentChange}
        handlePrincipalPaymentBlur={handlePrincipalPaymentBlur}
        handleInterestRateBlur={handleInterestRateBlur}
        handleKeyDown={(e, rowIndex, colIndex) =>
          handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, rowIndex, colIndex)
        }
        isDisabled={isDisabled}
        // Focus management
        handleInputFocus={handleInputFocus}
        handleInputBlur={handleInputBlur}
        isNavigating={isNavigating}
      />

      {/* Validation Messages */}
      {(showValidation || allFieldsTouched) && validationErrors.length > 0 && (
        <Box
          mt={4}
          p={3}
          bg="red.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="red.500"
        >
          <Text fontSize="xs" fontWeight="bold" color="red.600" mb={2}>
            Please fix the following issues:
          </Text>
          <VStack align="flex-start" gap={1}>
            {validationErrors.map(
              (error: string): React.ReactNode => (
                <Text key={error} fontSize="xs" color="red.600">
                  • {error}
                </Text>
              )
            )}
          </VStack>
        </Box>
      )}

      <ExistingDebtFooter
        values={values}
        showClearAllDialog={showClearAllDialog}
        setShowClearAllDialog={setShowClearAllDialog}
        onClearAll={handleClearAll}
      />
    </VStack>
  );
};

// Utility function to convert form values to engine format
export const convertToEngineFormat = (formValues: ExistingDebtInputForm): ExistingDebtInput => {
  const converted = {
    id: formValues.id,
    name: formValues.name,
    existingDebtPaymentInputs: (formValues.payments || []).map((payment) => {
      // Use saved interest payment from reverse amortization calculation if available
      // Otherwise, builder will calculate it
      // Parse values - principalPayment might be formatted currency string, so clean it first
      const principalPaymentStr = payment.principalPayment || '';
      const principalPayment = parseFloat(principalPaymentStr.replace(/[^\d.-]/g, '')) || 0;
      const interestRate = parseFloat(payment.interestRate) / 100 || 0;
      const savedInterestPayment = parseFloat(payment.interestPayment) || 0;

      return {
        paymentDate: payment.paymentDate,
        interestRate,
        principalPayment,
        principalInterest: savedInterestPayment > 0 ? savedInterestPayment : 0, // Use saved value or let builder calculate
      };
    }),
  };

  return converted;
};

// Utility function to convert engine format to form values
export const convertFromEngineFormat = (engineValues: ExistingDebtInput): ExistingDebtInputForm => {
  return {
    id: engineValues.id || '',
    name: engineValues.name || '',
    payments: (engineValues.existingDebtPaymentInputs || []).map(
      (payment: ExistingDebtPaymentInput) => ({
        paymentDate: payment.paymentDate || '',
        interestRate: ((payment.interestRate || 0) * 100).toString(),
        interestPayment: (payment.principalInterest || 0).toString(),
        principalPayment: (payment.principalPayment || 0).toString(),
      })
    ),
  };
};

// Create empty form values
export const createEmptyExistingDebtInputForm = (): ExistingDebtInputForm => {
  return {
    id: '',
    name: '',
    payments: [],
  };
};

export type { ExistingDebtInputForm, ExistingDebtPaymentForm };
